'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

import {useEventsAnalytics} from '@web-catalog/components/Events/useEventsAnalytics';

import {EventColoredContentType, RecommendedApp} from '@web-catalog/types';

import {ROUTES} from '@web-catalog/routes';

import {useIsVisibleInScroller} from '@web-catalog/ui-kit/Scroller/ScrollerContext';

import styles from './EventsCard.module.scss';

type Props = {
  id: number | string;
  badge?: string;
  title: EventColoredContentType;
  subtitle: EventColoredContentType;
  backgroundImage: string;
  gradientColor: string;
  appInfo: RecommendedApp;
  cardNum: number;
  testId?: string;
};

export const EventsCard = ({
  id,
  badge,
  title,
  subtitle,
  backgroundImage,
  gradientColor,
  appInfo,
  cardNum,
  testId,
}: Props) => {
  const {appName, iconUrl, shortDescription, appId} = appInfo;
  const {logEventClick} = useEventsAnalytics();
  const handleCardClick = () => {
    logEventClick({
      appId: String(appId),
      compilation_id: id,
      compilation_name: title.value,
      compilation_num: cardNum,
    });
  };

  const isVisible = useIsVisibleInScroller(cardNum - 1);

  return (
    <div className={styles.container}>
      <Link
        href={`${ROUTES.event.index(id)}?eventNum=${cardNum}`}
        prefetch={false}
        className={styles.link}
        data-testid={testId || 'eventsCard'}
        onClick={handleCardClick}
        style={
          {
            backgroundImage: isVisible ? `url(${backgroundImage})` : 'none',
            '--gradient-bottom-color': gradientColor,
            '--gradient-top-color': `${gradientColor}00`,
            '--text-color': title?.color || subtitle?.color,
          } as React.CSSProperties
        }
      >
        {isVisible && (
          <>
            <div className={styles.badge}>{badge}</div>
            <div className={styles.underlay} />
            <div className={styles.content}>
              <p className={styles.title}>{title.value}</p>
              <p className={styles.subtitle}>{subtitle.value}</p>
              <div className={styles.app}>
                <Image
                  src={iconUrl}
                  width={50}
                  height={50}
                  alt="App icon"
                  loading="lazy"
                  className={styles.app__icon}
                />
                <div className={styles.app__content}>
                  <p className={styles.app__name}>{appName}</p>
                  <p className={styles.app__description}>{shortDescription}</p>
                </div>
              </div>
            </div>
          </>
        )}
      </Link>
    </div>
  );
};
